import React, { useState, useContext, useEffect, useMemo } from "react";
import axios from "axios";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import ProjectPreOrderLayoutWrapper from "../../components/ProjectPreOrderLayoutWrapper";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaSave,
  FaPlus,
  FaArrowLeft,
  FaEdit,
  FaMinus,
  FaTrash,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const TenderDetailEntry = () => {
  const API = "https://nissi-india.com/api/tenderDetailEntry";

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const navigate = useNavigate();

  // -----------------------------
  // localStorage helper
  // -----------------------------
  const getLS = (key) => {
    try {
      return localStorage.getItem(key) || "";
    } catch {
      return "";
    }
  };

  // localStorage values
  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchId = getLS("branchId") || getLS("branchid");
  const branchType = getLS("branchType") || getLS("branchtype");
  const userId = getLS("userId") || getLS("loginuserid");

  const branchid = Number(branchId || 0);

  // common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branchtype": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  // -----------------------------
  // MAIN FORM STATE (header)
  // -----------------------------
  const [formData, setFormData] = useState({
    preEstimationId: "",
    tenderRefNo: "",
    clientName: "",
    workLocationId: "",
    otherWorkLocationName: "",
    workLocationCode: "",

    estimatedValue: "",
    estimatedRemarks: "",
    emdValue: "",
    emdRemarks: "",
    emdAlertDate: "",

    costOfTender: "",
    tenderRemarks: "",
    consultantName: "",
    projectDuration: "",

    tenderSubmissionDate: "",
    dateOfIssue: "",
    bidValidityFrom: "",
    bidValidityTo: "",
    bidValidityDays: "",

    tenderSystem: "",
    technicalBidOpening: "",

    financialTurnover: "",
    technicalQualification: "",
    equipmentCapability: "",
    manpowerCapability: "",
    siteVisitPreBidRemarks: "",

    preBidMeeting: "",
    preBidMeetingDate: "",

    mobilisationAdvance: "",
    mobilisationAdvance1: "",
    retentionMoney: "",
    retentionMoney1: "",
    performanceGuarantee: "",
    performanceGuarantee1: "",
    releaseOfPbg: "",
    releaseOfPbg1: "",
    releaseOfRetentionMoney: "",
    releaseOfRetentionMoney1: "",
    defectLiabilityPeriod: "",
    groupInsurance: "",

    shopDrawing: "",
  });

  const [isEditMode, setIsEditMode] = useState(true);
  const [isViewLoaded, setIsViewLoaded] = useState(false);

  // -----------------------------
  // WORK LOCATION
  // -----------------------------
  const [workLocations, setWorkLocations] = useState([]);
  const [showOtherWorkLocation, setShowOtherWorkLocation] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/workLocations`, {
          headers: commonHeaders,
        });
        setWorkLocations(res.data?.data || []);
      } catch (e) {
        console.error(e);
        toast.error("Failed to load Work Locations");
      }
    })();
  }, [API, commonHeaders]);

  useEffect(() => {
    if (formData.preBidMeeting === "No") {
      handleInputChange("preBidMeetingDate", "");
    }
  }, [formData.preBidMeeting]);

  useEffect(() => {
    setShowOtherWorkLocation(formData.workLocationId === "OTHERS");
  }, [formData.workLocationId]);

  // -----------------------------
  // TENDER BASIC DOCUMENTS
  // -----------------------------
  const [tenderBasicPick, setTenderBasicPick] = useState(null);
  const [tenderBasicFiles, setTenderBasicFiles] = useState([]);
  const [tenderBasicSelected, setTenderBasicSelected] = useState([]);

  const uploadTenderBasicDoc = async () => {
    try {
      if (!tenderBasicPick) {
        toast.error("Please choose a file first");
        return;
      }

      const fd = new FormData();
      fd.append("file", tenderBasicPick);

      const res = await axios.post(`${API}/upload/tenderBasic`, fd, {
        headers: {
          ...commonHeaders,
          "Content-Type": "multipart/form-data",
        },
      });

      const filename = res.data?.data?.filename;
      if (!filename) throw new Error("No filename returned");

      setTenderBasicFiles((p) => [...p, filename]);
      setTenderBasicPick(null);
      setTenderBasicSelected([]);
      toast.success("Uploaded");
    } catch (err) {
      console.error(err);
      toast.error("Failed to upload document");
    }
  };

  const removeTenderBasicSelected = () => {
    if (!tenderBasicSelected.length) {
      toast.error("Select file(s) to remove");
      return;
    }
    setTenderBasicFiles((p) => p.filter((f) => !tenderBasicSelected.includes(f)));
    setTenderBasicSelected([]);
  };

  // -----------------------------
  // SITE VISIT DOCUMENTS
  // -----------------------------
  const [siteVisitSelectedFile, setSiteVisitSelectedFile] = useState(null);
  const [siteVisitFiles, setSiteVisitFiles] = useState([]);
  const [siteVisitSelectedIndex, setSiteVisitSelectedIndex] = useState(-1);

  const uploadSiteVisitDoc = async () => {
    try {
      if (!siteVisitSelectedFile) {
        toast.error("Please choose a file");
        return;
      }

      const form = new FormData();
      form.append("file", siteVisitSelectedFile);

      const res = await axios.post(`${API}/siteVisit/upload`, form, {
        headers: {
          ...commonHeaders,
          "Content-Type": "multipart/form-data",
        },
      });

      const filename = res.data?.data?.filename;
      if (!filename) throw new Error("No filename returned");

      setSiteVisitFiles((prev) => [...prev, filename]);
      setSiteVisitSelectedFile(null);
      setSiteVisitSelectedIndex(-1);
      toast.success("Uploaded");
    } catch (err) {
      console.error(err);
      toast.error("Upload failed");
    }
  };

  const removeSiteVisitDoc = () => {
    if (siteVisitSelectedIndex < 0) {
      toast.error("Select a file to remove");
      return;
    }
    setSiteVisitFiles((prev) => prev.filter((_, i) => i !== siteVisitSelectedIndex));
    setSiteVisitSelectedIndex(-1);
  };

  // -----------------------------
  // TAX DETAILS
  // -----------------------------
  const [taxDetails, setTaxDetails] = useState([
    { taxtypeId: "0", description1: "", description: "", otherTaxTypeName: "" },
    { taxtypeId: "0", description1: "", description: "", otherTaxTypeName: "" },
    { taxtypeId: "0", description1: "", description: "", otherTaxTypeName: "" },
    { taxtypeId: "0", description1: "", description: "", otherTaxTypeName: "" },
    { taxtypeId: "0", description1: "", description: "", otherTaxTypeName: "" },
  ]);

  const [taxTypeOptions, setTaxTypeOptions] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/taxTypes`, {
          headers: commonHeaders,
        });
        setTaxTypeOptions(res.data?.data || []);
      } catch (e) {
        console.error(e);
        toast.error("Failed to load Tax Types");
      }
    })();
  }, [API, commonHeaders]);

  const handleTaxChange = (index, field, value) => {
    setTaxDetails((prev) =>
      prev.map((r, i) => (i === index ? { ...r, [field]: value } : r))
    );
  };

  // -----------------------------
  // MAJOR ITEMS OF WORK INVOLVED
  // -----------------------------
  const truncate = (s = "", n = 60) => (s.length > n ? s.slice(0, n - 1) + "…" : s);

  const [workRows, setWorkRows] = useState([
    { workItemId: "0", workItemOther: "", uomId: "0", uomOther: "", qty: "" },
  ]);

  const [workItemList, setWorkItemList] = useState([]);
  const [uomList, setUomList] = useState([]);

  const addWorkRow = () =>
    setWorkRows((p) => [
      ...p,
      { workItemId: "0", workItemOther: "", uomId: "0", uomOther: "", qty: "" },
    ]);

  const removeWorkRow = (idx) => setWorkRows((p) => p.filter((_, i) => i !== idx));

  const updateWorkRow = (idx, key, val) =>
    setWorkRows((p) => p.map((r, i) => (i === idx ? { ...r, [key]: val } : r)));

  useEffect(() => {
    (async () => {
      try {
        const [wiRes, uomRes] = await Promise.all([
          axios.get(`${API}/workInvolvedItems`, { headers: commonHeaders }),
          axios.get(`${API}/uoms`, { headers: commonHeaders }),
        ]);

        setWorkItemList(wiRes.data?.data || []);
        setUomList(uomRes.data?.data || []);
      } catch (err) {
        console.error(err);
        toast.error("Failed to load Major Items dropdowns");
      }
    })();
  }, [API, commonHeaders]);

  // -----------------------------
  // OTHER DETAILS MASTER + ROWS
  // -----------------------------
  const [otherMaster, setOtherMaster] = useState([]);
  const [otherRows, setOtherRows] = useState([
    { otherDetailsId: "0", otherDetailsText: "", item1: "", item2: "" },
  ]);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/otherDetailsMaster`, {
          headers: commonHeaders,
        });
        setOtherMaster(res.data?.data || []);
      } catch (e) {
        console.error(e);
        toast.error("Failed to load Other Details master");
      }
    })();
  }, [API, commonHeaders]);

  const updateOtherRow = (idx, patch) =>
    setOtherRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  const addOtherRow = () =>
    setOtherRows((prev) => [
      ...prev,
      { otherDetailsId: "0", otherDetailsText: "", item1: "", item2: "" },
    ]);

  const removeOtherRow = (idx) => setOtherRows((prev) => prev.filter((_, i) => i !== idx));

  // -----------------------------
  // SUPPLIER ROWS + FILES
  // -----------------------------
  const [supplierRows, setSupplierRows] = useState([{ supplierName: "", files: [] }]);

  const updateSupplierRow = (idx, patch) => {
    setSupplierRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const addSupplierRow = () =>
    setSupplierRows((prev) => [...prev, { supplierName: "", files: [] }]);

  const removeSupplierRow = (idx) =>
    setSupplierRows((prev) => prev.filter((_, i) => i !== idx));

  const uploadSupplierFile = async (supplierIndex, file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);

      const res = await axios.post(`${API}/supplier/upload`, fd, {
        headers: {
          ...commonHeaders,
          "Content-Type": "multipart/form-data",
        },
      });

      const savedName = res.data?.data?.filename;
      if (!savedName) throw new Error("No filename returned");

      const row = supplierRows[supplierIndex];
      updateSupplierRow(supplierIndex, { files: [...row.files, savedName] });

      toast.success("File added");
    } catch (err) {
      console.error(err);
      toast.error("File upload failed");
    }
  };

  const removeSupplierFile = (supplierIndex, fileIndex) => {
    const row = supplierRows[supplierIndex];
    updateSupplierRow(supplierIndex, { files: row.files.filter((_, i) => i !== fileIndex) });
  };

  // -----------------------------
  // PRE-ESTIMATION (Name of Work)
  // -----------------------------
  const [preEstimations, setPreEstimations] = useState([]);
  const [selectedPreEstId, setSelectedPreEstId] = useState("");
  const [estimationGrid, setEstimationGrid] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const rs = await axios.get(`${API}/preEstimations`, {
          headers: commonHeaders,
        });
        setPreEstimations(rs.data?.data || []);
      } catch (e) {
        toast.error("Failed to load Name of Work list");
      }
    })();
  }, [API, commonHeaders]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);

    const contractId = urlParams.get("contractId") || "";
    const tenderId = urlParams.get("tenderId") || "";

    if (!contractId || !tenderId) return;

    sessionStorage.setItem("contractid", contractId);
    sessionStorage.setItem("tenderid", tenderId);

    (async () => {
      try {
        const res = await axios.post(
          `${API}/view`,
          { contractId, tenderId },
          { headers: commonHeaders }
        );

        const d = res.data?.data;
        if (!res.data?.success || !d) {
          toast.error(res.data?.message || "Failed to load view");
          return;
        }

        setSelectedPreEstId(String(d.preEstimationId || ""));

        setFormData((p) => ({
          ...p,
          ...d.header,
          preEstimationId: String(d.preEstimationId || d.header?.preEstimationId || ""),
        }));

        setTenderBasicFiles(d.tenderBasicDocs || []);
        setTenderBasicSelected([]);

        setSiteVisitFiles(d.siteVisitDocs || []);
        setSiteVisitSelectedIndex(-1);

        const tax = d.taxRows || [];
        const paddedTax = [
          ...tax,
          ...Array.from({ length: Math.max(0, 5 - tax.length) }, () => ({
            taxtypeId: "0",
            description1: "",
            description: "",
            otherTaxTypeName: "",
          })),
        ].slice(0, 5);
        setTaxDetails(paddedTax);

        const other = d.otherDetailsRows || [];
        setOtherRows(
          other.length
            ? other.map((x) => ({
                otherDetailsId: x.otherDetailsId || "0",
                otherDetailsText: x.otherName || "",
                item1: x.item1 || "",
                item2: x.item2 || "",
              }))
            : [{ otherDetailsId: "0", otherDetailsText: "", item1: "", item2: "" }]
        );

        const supp = d.suppliers || [];
        setSupplierRows(
          supp.length
            ? supp.map((s) => ({
                supplierName: s.supplierName || "",
                files: s.documents || [],
              }))
            : [{ supplierName: "", files: [] }]
        );

        const mw = d.majorWorkItems || [];
        setWorkRows(
          mw.length
            ? mw
            : [{ workItemId: "0", workItemOther: "", uomId: "0", uomOther: "", qty: "" }]
        );
      } catch (err) {
        console.error("view load error:", err);
        toast.error(err?.response?.data?.message || err.message || "View request failed");
      }

      setIsViewLoaded(true);
      setIsEditMode(false);

      if (!contractId || !tenderId) {
        setIsEditMode(true);
        return;
      }
    })();
  }, [API, commonHeaders]);

  const isDisabled = isViewLoaded && !isEditMode;

  const formatINR = (n) => {
    const num = Number(n || 0);
    return (
      "Rs. " +
      num.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    );
  };

  const handlePreEstChange = async (id) => {
    setSelectedPreEstId(id);
    handleInputChange("preEstimationId", id);

    if (!id) {
      setEstimationGrid([]);
      setFormData((prev) => ({
        ...prev,
        preEstimationId: "",
        tenderRefNo: "",
        clientName: "",
        estimatedValue: "Rs. ",
      }));
      return;
    }

    try {
      const rs = await axios.get(`${API}/estimationDet`, {
        params: { preEstimationId: id },
        headers: commonHeaders,
      });
      const payload = rs.data?.data;

      setFormData((prev) => ({
        ...prev,
        preEstimationId: id,
        tenderRefNo: payload?.header?.tenderRefNo || "",
        clientName: payload?.header?.clientName || "",
        estimatedValue: formatINR(payload?.header?.estimatedValue),
        preEstimationLabel:
          preEstimations.find((x) => String(x.id) === String(id))?.label || "",
      }));

      setEstimationGrid(payload?.grid || []);
    } catch (e) {
      toast.error("Failed to load estimation details");
    }
  };

  const handleInputChange = (key, value) => {
    setFormData((p) => ({ ...p, [key]: value }));
  };

  const validateForm = () => {
    if (!selectedPreEstId) {
      toast.error("Please Enter Name of the Work");
      return false;
    }

    if (!formData.tenderRefNo) {
      toast.error("Please Enter the Tender Ref No");
      return false;
    }

    if (!formData.workLocationId) {
      toast.error("Please Enter Location");
      return false;
    }

    if (formData.workLocationId === "OTHERS" && !formData.otherWorkLocationName?.trim()) {
      toast.error("Please enter Work Location Name");
      return false;
    }

    if (!formData.estimatedValue) {
      toast.error("Please Enter the Estimated Value");
      return false;
    }

    if (!formData.emdValue) {
      toast.error("Please Enter the EMD Value");
      return false;
    }

    if (!formData.costOfTender) {
      toast.error("Please Enter the Cost Of Tender");
      return false;
    }

    if (!formData.tenderSubmissionDate) {
      toast.error("Please Enter the Submission Date");
      return false;
    }

    if (!formData.dateOfIssue) {
      toast.error("Please Enter the Date of Issue");
      return false;
    }

    if (!formData.bidValidityFrom) {
      toast.error("Please Enter Bid Validity From Date");
      return false;
    }

    if (!formData.bidValidityTo) {
      toast.error("Please Enter Bid Validity To Date");
      return false;
    }

    if (!formData.preBidMeeting) {
      toast.error("Please Select Yes or No for Pre-Bid Meeting");
      return false;
    }

    if (formData.preBidMeeting === "Yes" && !formData.preBidMeetingDate) {
      toast.error("Please Enter the Pre-Bid Meeting Date");
      return false;
    }

    return true;
  };

  const selectedWork = preEstimations.find((x) => String(x.id) === String(selectedPreEstId));
  const nameOfWorkText = selectedWork?.label || "";

  useEffect(() => {
    const days = parseInt(formData.bidValidityDays, 10);
    const from = formData.bidValidityFrom;

    if (!from || !days || days <= 0) {
      handleInputChange("bidValidityTo", "");
      return;
    }

    const fromDate = new Date(from);
    fromDate.setDate(fromDate.getDate() + days);

    const toISO = fromDate.toISOString().slice(0, 10);
    handleInputChange("bidValidityTo", toISO);
  }, [formData.bidValidityDays, formData.bidValidityFrom]);

  const cleanMoneyToNumber = (v) => {
    if (!v) return 0;
    return Number(String(v).replaceAll("Rs.", "").replaceAll(",", "").trim() || 0);
  };

  const toNullIfEmpty = (v) => {
    const s = String(v ?? "").trim();
    return s === "" ? null : s;
  };

  const toIntOrNull = (v) => {
    const n = parseInt(String(v ?? "").trim(), 10);
    return Number.isFinite(n) ? n : null;
  };

  const projectPayload = {
    preEstimationId: toIntOrNull(formData.preEstimationId || selectedPreEstId),
    contractName: toNullIfEmpty(formData.preEstimationLabel || nameOfWorkText || ""),
    clientName: toNullIfEmpty(formData.clientName),
    workLocationId:
      formData.workLocationId === "OTHERS" ? null : toIntOrNull(formData.workLocationId),
    otherWorkLocationName:
      formData.workLocationId === "OTHERS"
        ? toNullIfEmpty(formData.otherWorkLocationName)
        : null,
    workLocationCode:
      formData.workLocationId === "OTHERS" ? toNullIfEmpty(formData.workLocationCode) : null,

    contractValue: cleanMoneyToNumber(formData.estimatedValue),
    contractValue1: toNullIfEmpty(formData.estimatedValue),
    contractValueRemarks: toNullIfEmpty(formData.estimatedRemarks),

    emdBidSecurityAmt: cleanMoneyToNumber(formData.emdValue),
    emdBidSecurityAmt1: toNullIfEmpty(formData.emdValue),
    emdBidSecurityAmtRemarks: toNullIfEmpty(formData.emdRemarks),

    contractDuration: toNullIfEmpty(formData.projectDuration),
    mobilisationAdvance: toNullIfEmpty(formData.mobilisationAdvance),
    mobilisationAdvance1: toNullIfEmpty(formData.mobilisationAdvance1),
    retentionAmount: toNullIfEmpty(formData.retentionMoney),
    retentionAmount1: toNullIfEmpty(formData.retentionMoney1),
    defectLiabilityPeriod: toNullIfEmpty(formData.defectLiabilityPeriod),
  };

  const tenderPayload = {
    tenderRefNo: toNullIfEmpty(formData.tenderRefNo),
    consultantName: toNullIfEmpty(formData.consultantName),

    costOfTenderDocument: cleanMoneyToNumber(formData.costOfTender),
    costOfTenderDocument1: toNullIfEmpty(formData.costOfTender),

    remarks: toNullIfEmpty(formData.tenderRemarks),

    financialTurnover: toNullIfEmpty(formData.financialTurnover),
    financialTurnover1: toNullIfEmpty(formData.financialTurnover1),

    technicalQualification: toNullIfEmpty(formData.technicalQualification),
    technicalQualification1: toNullIfEmpty(formData.technicalQualification1),

    siteVisitPreBidMeetingDate:
      formData.preBidMeeting === "Yes" ? toNullIfEmpty(formData.preBidMeetingDate) : null,

    siteVisitPreBidMeetingDate1: toNullIfEmpty(formData.siteVisitPreBidDate1),
    siteVisitPreBidMeetingRemarks: toNullIfEmpty(formData.siteVisitPreBidRemarks),
    siteVisitPreBidMeetingRemarks1: toNullIfEmpty(formData.siteVisitPreBidRemarks1),

    equipmentCapability: toNullIfEmpty(formData.equipmentCapability),
    equipmentCapability1: toNullIfEmpty(formData.equipmentCapability1),

    manpowerCapability: toNullIfEmpty(formData.manpowerCapability),
    manpowerCapability1: toNullIfEmpty(formData.manpowerCapability1),

    bidValidityFrom: toNullIfEmpty(formData.bidValidityFrom),
    bidValidityTo: toNullIfEmpty(formData.bidValidityTo),
    bidValidityDays: toNullIfEmpty(formData.bidValidityDays),
    bidValidityDays1: toNullIfEmpty(formData.bidValidityDays1),

    tenderSubmissionDateVenue: toNullIfEmpty(formData.tenderSubmissionDate),
    tenderIssueOrUploadingDate: toNullIfEmpty(formData.dateOfIssue),

    tenderSystemTwoSingle: toNullIfEmpty(formData.tenderSystem),
    technicalBidOpening: toNullIfEmpty(formData.technicalBidOpening),

    performanceGuarantee: toNullIfEmpty(formData.performanceGuarantee),
    performanceGuarantee1: toNullIfEmpty(formData.performanceGuarantee1),

    releaseOfPpg: toNullIfEmpty(formData.releaseOfPbg),
    releaseOfPpg1: toNullIfEmpty(formData.releaseOfPbg1),

    releaseOfRetentionMoney: toNullIfEmpty(formData.releaseOfRetentionMoney),
    releaseOfRetentionMoney1: toNullIfEmpty(formData.releaseOfRetentionMoney1),

    groupInsurance: toNullIfEmpty(formData.groupInsurance),

    emdAlertDate: toNullIfEmpty(formData.emdAlertDate),
  };

  const tenderBasicDocs = tenderBasicFiles.map((filename) => ({ filename }));
  const siteVisitDocs = siteVisitFiles.map((filename) => ({ filename }));

  const taxPayload = taxDetails
    .filter((x) => String(x.taxtypeId) !== "0")
    .map((x) => ({
      taxtypeId: String(x.taxtypeId) === "123456789" ? null : toIntOrNull(x.taxtypeId),
      otherTaxTypeName:
        String(x.taxtypeId) === "123456789" ? toNullIfEmpty(x.otherTaxTypeName) : null,
      description: toNullIfEmpty(x.description),
      description1: toNullIfEmpty(x.description1),
    }));

  const otherDetailsPayload = otherRows
    .filter((x) => String(x.otherDetailsId) !== "0")
    .map((x) => ({
      otherDetailsId: x.otherDetailsId === "OTHERS" ? null : toIntOrNull(x.otherDetailsId),
      otherDetailsText:
        x.otherDetailsId === "OTHERS" ? toNullIfEmpty(x.otherDetailsText) : null,
      item1: toNullIfEmpty(x.item1),
      item2: toNullIfEmpty(x.item2),
    }));

  const supplierPayload = supplierRows
    .filter((s) => (s.supplierName || "").trim() !== "")
    .map((s) => ({
      supplierName: toNullIfEmpty(s.supplierName),
      files: (s.files || []).map((filename) => ({ filename })),
    }));

  const payload = {
    branchId: toIntOrNull(getLS("branchId") || getLS("branchid")),
    userId: toIntOrNull(getLS("userId") || getLS("loginuserid")),

    contractId: toIntOrNull(sessionStorage.getItem("contractid") || null),
    tenderId: toIntOrNull(sessionStorage.getItem("tenderid") || null),

    project: projectPayload,
    tender: tenderPayload,

    docs: {
      tenderBasic: tenderBasicDocs,
      siteVisit: siteVisitDocs,
    },

    taxDetails: taxPayload,
    otherDetails: otherDetailsPayload,
    suppliers: supplierPayload,

    shopDrawing: toNullIfEmpty(formData.shopDrawing),
    majorWorkItems: workRows,
  };

  const buildSaveAllPayload = () => {
    const lsUserId = parseInt(getLS("userId") || getLS("loginuserid") || "0", 10);
    const lsBranchId = parseInt(getLS("branchId") || getLS("branchid") || "0", 10);

    const contractId = parseInt(sessionStorage.getItem("contractid") || "0", 10) || 0;
    const tenderId = parseInt(sessionStorage.getItem("tenderid") || "0", 10) || 0;

    const selectedWork = preEstimations.find((x) => String(x.id) === String(selectedPreEstId));
    const nameOfWorkText = selectedWork?.label || "";

    return {
      userId: lsUserId,
      branchId: lsBranchId,
      finYearId,
      finYear,
      branchType,
      ipAddress: getLS("ipAddress") || "",

      contractId,
      tenderId,

      nameOfWorkText,
      clientName: formData.clientName || "",

      workLocationId: formData.workLocationId || "",
      workLocationIsOther: formData.workLocationId === "OTHERS",
      workLocationName: formData.otherWorkLocationName || "",
      workLocationCode: formData.workLocationCode || "",

      estimatedValueText: formData.estimatedValue || "",
      estimatedRemarks: formData.estimatedRemarks || "",
      emdValueText: formData.emdValue || "",
      emdRemarks: formData.emdRemarks || "",
      projectDuration: formData.projectDuration || "",

      mobilisationAdvance: formData.mobilisationAdvance || "",
      mobilisationAdvance1: formData.mobilisationAdvance1 || "",
      retentionMoney: formData.retentionMoney || "",
      retentionMoney1: formData.retentionMoney1 || "",
      defectLiabilityPeriod: formData.defectLiabilityPeriod || "",

      tenderRefNo: formData.tenderRefNo || "",
      consultantName: formData.consultantName || "",
      costOfTenderText: formData.costOfTender || "",
      remarks: formData.tenderRemarks || "",

      financialTurnover: formData.financialTurnover || "",
      financialTurnover1: formData.financialTurnover1 || "",
      technicalQualification: formData.technicalQualification || "",
      technicalQualification1: formData.technicalQualification1 || "",

      preBidYes: formData.preBidMeeting === "Yes",
      preBidMeetingDate: formData.preBidMeetingDate || "",
      preBidMeetingDateText: "",
      preBidMeetingRemarks: formData.siteVisitPreBidRemarks || "",
      preBidMeetingRemarks1: "",

      equipmentCapability: formData.equipmentCapability || "",
      equipmentCapability1: "",
      manpowerCapability: formData.manpowerCapability || "",
      manpowerCapability1: "",

      bidValidityFrom: formData.bidValidityFrom || "",
      bidValidityTo: formData.bidValidityTo || "",
      bidValidityDays: formData.bidValidityDays || "",
      bidValidityDays1: "",

      tenderSubmissionDateVenue: formData.tenderSubmissionDate || "",
      tenderIssueOrUploadingDate: formData.dateOfIssue || "",

      tenderSystemTwoSingle: formData.tenderSystem || "",
      technicalBidOpening: formData.technicalBidOpening || "",

      performanceGuarantee: formData.performanceGuarantee || "",
      performanceGuarantee1: formData.performanceGuarantee1 || "",
      releaseOfPpg: formData.releaseOfPbg || "",
      releaseOfPpg1: formData.releaseOfPbg1 || "",
      releaseOfRetentionMoney: formData.releaseOfRetentionMoney || "",
      releaseOfRetentionMoney1: formData.releaseOfRetentionMoney1 || "",
      groupInsurance: formData.groupInsurance || "",
      emdAlertDate: formData.emdAlertDate || "",

      preEstimationId: selectedPreEstId || "",

      tenderBasicDocs: tenderBasicFiles,
      siteVisitDocs: siteVisitFiles,

      taxRows: taxDetails.map((t) => ({
        taxtypeId: t.taxtypeId,
        description: t.description,
        description1: t.description1,
        otherTaxTypeName: t.otherTaxTypeName || "",
      })),

      otherDetailsRows: otherRows.map((o) => ({
        otherDetailsId: o.otherDetailsId,
        otherName: o.otherDetailsText || "",
        item1: o.item1 || "",
        item2: o.item2 || "",
      })),

      majorWorkItems: workRows,

      suppliers: supplierRows.map((s) => ({
        supplierName: s.supplierName,
        documents: s.files || [],
      })),
    };
  };

  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async (e) => {
    e?.preventDefault?.();

    if (isSaving) return;

    const ok = validateForm();
    if (!ok) return;

    setIsSaving(true);

    try {
      const payload = buildSaveAllPayload();

      const res = await axios.post(`${API}/saveAll`, payload, {
        headers: commonHeaders,
      });

      if (res.data?.success) {
        toast.success(res.data?.message || "Saved Successfully");

        const data = res.data?.data || {};

        if (data.contractId) {
          sessionStorage.setItem("contractid", String(data.contractId));
        }

        if (data.tenderId) {
          sessionStorage.setItem("tenderid", String(data.tenderId));
        }

        if (data.workLocationId || data.workLocationCode) {
          setFormData((p) => ({
            ...p,
            workLocationId: data.workLocationId
              ? String(data.workLocationId)
              : p.workLocationId,
            workLocationCode: data.workLocationCode
              ? String(data.workLocationCode)
              : p.workLocationCode,
          }));
        }

        setTimeout(() => {
          navigate("/OrderManagement/TenderDetailsListing");
        }, 800);
      } else {
        toast.error(res.data?.message || "Save failed");
      }
    } catch (err) {
      console.error("saveAll error =>", err);

      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Request failed";

      toast.error(msg);
    } finally {
      setIsSaving(false);
    }
  };

  const handleBack = () => navigate(-1);

  return (
    <ProjectPreOrderLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">TENDER DETAIL ENTRY</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />
       <button
                  type="button"
                  onClick={handleBack}
                 className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
            isDark
              ? "border-slate-600 text-slate-300 hover:bg-slate-800"
              : "border-slate-300 text-slate-700 hover:bg-slate-100"
          }`}
                >
                  <FaArrowLeft />
                  Back
                </button>
        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-4">
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Tender Detail Entry
              </h1>
            </div>
          </div>

          <div className="space-y-8">
            {/* Basic Details Section */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Name of The Work
                  </label>

                  <div className="relative">
                    <select
                      value={selectedPreEstId}
                      disabled={isDisabled}
                      onChange={(e) => handlePreEstChange(e.target.value)}
                      className={`w-full px-4 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                          : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                      }`}
                    >
                      <option value="">Select</option>
                      {preEstimations.map((x) => (
                        <option key={x.id} value={x.id}>
                          {x.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Tender Ref No
                  </label>
                  <input
                    type="text"
                    disabled={isDisabled}
                    value={formData.tenderRefNo}
                    onChange={(e) => handleInputChange("tenderRefNo", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Client Name
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      disabled={isDisabled}
                      value={formData.clientName}
                      onChange={(e) => handleInputChange("clientName", e.target.value)}
                      className={`w-full px-4 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                          : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                      }`}
                    />
                    <FaEdit className="absolute right-3 top-3 text-gray-400" />
                  </div>
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Estimated Value *
                  </label>
                  <input
                    type="text"
                    disabled={isDisabled}
                    value={formData.estimatedValue}
                    onChange={(e) => handleInputChange("estimatedValue", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    EMD Value *
                  </label>
                  <input
                    type="text"
                    disabled={isDisabled}
                    value={formData.emdValue}
                    onChange={(e) => handleInputChange("emdValue", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    EMD Alert Date *
                  </label>
                  <input
                    type="date"
                    disabled={isDisabled}
                    value={formData.emdAlertDate}
                    onChange={(e) => handleInputChange("emdAlertDate", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Cost Of Tender *
                  </label>
                  <input
                    type="text"
                    disabled={isDisabled}
                    value={formData.costOfTender}
                    onChange={(e) => handleInputChange("costOfTender", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Product Duration
                  </label>
                  <input
                    type="text"
                    disabled={isDisabled}
                    value={formData.projectDuration}
                    onChange={(e) => handleInputChange("projectDuration", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Tender Submission Date *
                  </label>
                  <input
                    type="date"
                    disabled={isDisabled}
                    value={formData.tenderSubmissionDate}
                    onChange={(e) => handleInputChange("tenderSubmissionDate", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Technical Bid Opening
                  </label>
                  <textarea
                    value={formData.technicalBidOpening}
                    disabled={isDisabled}
                    onChange={(e) => handleInputChange("technicalBidOpening", e.target.value)}
                    rows={3}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-4">
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Work Location
                </label>
                <select
                  value={formData.workLocationId}
                  disabled={isDisabled}
                  onChange={(e) => {
                    const v = e.target.value;
                    handleInputChange("workLocationId", v);

                    const isOthers = v === "OTHERS";
                    setShowOtherWorkLocation(isOthers);

                    if (!isOthers) {
                      handleInputChange("otherWorkLocationName", "");
                      handleInputChange("workLocationCode", "");
                      return;
                    }

                    handleInputChange("workLocationCode", "");
                  }}
                  className={`w-full px-4 py-2 border rounded-lg ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                >
                  <option value="">Select</option>
                  {workLocations.map((w) => (
                    <option key={w.id} value={String(w.id)}>
                      {w.name}
                    </option>
                  ))}
                  <option value="OTHERS">Others</option>
                </select>

                {showOtherWorkLocation && (
                  <>
                    <input
                      type="text"
                      value={formData.workLocationCode || "Auto generated on Save"}
                      readOnly
                      className={`w-full mt-2 px-4 py-2 border rounded-lg ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-white"
                          : "bg-white border-gray-300 text-gray-900"
                      }`}
                    />
                    <input
                      type="text"
                      value={formData.otherWorkLocationName}
                      onChange={(e) =>
                        handleInputChange("otherWorkLocationName", e.target.value)
                      }
                      placeholder="Enter Work Location"
                      disabled={isDisabled}
                      className={`w-full mt-2 px-4 py-2 border rounded-lg ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-white"
                          : "bg-white border-gray-300 text-gray-900"
                      }`}
                    />
                  </>
                )}

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Consultant Name
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      disabled={isDisabled}
                      value={formData.consultantName}
                      onChange={(e) => handleInputChange("consultantName", e.target.value)}
                      className={`w-full px-4 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                          : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                      }`}
                    />
                    <FaEdit className="absolute right-3 top-3 text-gray-400" />
                  </div>
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Estimated Remarks
                  </label>
                  <div className="relative">
                    <textarea
                      rows={2}
                      disabled={isDisabled}
                      value={formData.estimatedRemarks}
                      onChange={(e) => handleInputChange("estimatedRemarks", e.target.value)}
                      className={`w-full px-4 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                          : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                      }`}
                    />
                    <FaEdit className="absolute right-3 top-3 text-gray-400" />
                  </div>
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    EMD Remarks
                  </label>
                  <div className="relative">
                    <textarea
                      rows={2}
                      disabled={isDisabled}
                      value={formData.emdRemarks}
                      onChange={(e) => handleInputChange("emdRemarks", e.target.value)}
                      className={`w-full px-4 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                          : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                      }`}
                    />
                    <FaEdit className="absolute right-3 top-3 text-gray-400" />
                  </div>
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Remarks
                  </label>
                  <div className="relative">
                    <textarea
                      rows={2}
                      disabled={isDisabled}
                      value={formData.tenderRemarks}
                      onChange={(e) => handleInputChange("tenderRemarks", e.target.value)}
                      className={`w-full px-4 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                          : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                      }`}
                    />
                    <FaEdit className="absolute right-3 top-3 text-gray-400" />
                  </div>
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Date of Issue of Tender/Uploading
                  </label>
                  <div className="relative">
                    <input
                      type="date"
                      disabled={isDisabled}
                      value={formData.dateOfIssue}
                      onChange={(e) => handleInputChange("dateOfIssue", e.target.value)}
                      className={`w-full px-4 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                          : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                      }`}
                    />
                    <FaEdit className="absolute right-3 top-3 text-gray-400" />
                  </div>
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Contract Liability Period
                  </label>
                  <input
                    type="text"
                    disabled={isDisabled}
                    value={formData.defectLiabilityPeriod}
                    onChange={(e) => handleInputChange("defectLiabilityPeriod", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Tender System (Two Cover/Single Cover)
                  </label>
                  <input
                    type="text"
                    disabled={isDisabled}
                    value={formData.tenderSystem}
                    onChange={(e) => handleInputChange("tenderSystem", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>

                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Upload Document
                </label>

                <div className="flex gap-2 items-start">
                  <div className="flex-1 space-y-2">
                    <input
                      type="file"
                      disabled={isDisabled}
                      onChange={(e) => setTenderBasicPick(e.target.files?.[0] || null)}
                      className={`w-full px-4 py-2 border rounded-lg ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-white file:bg-gray-600 file:text-white"
                          : "bg-white border-gray-300 text-gray-900 file:bg-gray-100 file:text-gray-700"
                      }`}
                    />

                    <select
                      multiple
                      value={tenderBasicSelected}
                      onChange={(e) => {
                        const selected = Array.from(e.target.selectedOptions).map(
                          (o) => o.value
                        );
                        setTenderBasicSelected(selected);
                      }}
                      className={`w-full h-24 px-3 py-2 border rounded-lg text-sm ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-white"
                          : "bg-white border-gray-300 text-gray-900"
                      }`}
                    >
                      {tenderBasicFiles.map((f) => (
                        <option key={f} value={f}>
                          {f}
                        </option>
                      ))}
                    </select>
                  </div>

                  {!isDisabled && (
                    <div className="flex flex-col gap-2">
                      <button
                        type="button"
                        onClick={uploadTenderBasicDoc}
                        className="p-2 rounded bg-blue-500 text-white hover:bg-blue-600"
                      >
                        <FaPlus />
                      </button>

                      <button
                        type="button"
                        onClick={removeTenderBasicSelected}
                        className="p-2 rounded bg-red-500 text-white hover:bg-red-600"
                      >
                        <FaMinus />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Pre-Qualification Requirement Section */}
            <div
              className={`p-4 rounded-lg border ${
                isDark ? "bg-gray-800 border-gray-700" : "bg-blue-50 border-blue-200"
              }`}
            >
              <h2 className={`text-lg font-semibold mb-4 ${isDark ? "text-white" : "text-blue-900"}`}>
                PRE-QUALIFICATION REQUIREMENT
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        isDark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Financial Turnover
                    </label>
                    <div className="relative">
                      <textarea
                        rows={2}
                        disabled={isDisabled}
                        value={formData.financialTurnover}
                        onChange={(e) => handleInputChange("financialTurnover", e.target.value)}
                        className={`w-full px-4 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none ${
                          isDark
                            ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                            : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                        }`}
                      />
                      <FaEdit className="absolute right-3 top-3 text-gray-400" />
                    </div>
                  </div>

                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        isDark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Pre Bid Meeting Date? *
                    </label>
                    <div className="flex gap-2">
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          disabled={isDisabled}
                          name="preBid"
                          value="Yes"
                          checked={formData.preBidMeeting === "Yes"}
                          onChange={(e) => handleInputChange("preBidMeeting", e.target.value)}
                        />
                        Yes
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          disabled={isDisabled}
                          name="preBid"
                          value="No"
                          checked={formData.preBidMeeting === "No"}
                          onChange={(e) => handleInputChange("preBidMeeting", e.target.value)}
                        />
                        No
                      </label>
                    </div>
                  </div>

                  {formData.preBidMeeting === "Yes" && (
                    <div>
                      <label
                        className={`block text-sm font-medium mb-2 ${
                          isDark ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Pre Bid Meeting Date
                      </label>

                      <input
                        type="date"
                        disabled={isDisabled}
                        value={formData.preBidMeetingDate}
                        onChange={(e) =>
                          handleInputChange("preBidMeetingDate", e.target.value)
                        }
                        className={`w-full px-4 py-2 border rounded-lg ${
                          isDark
                            ? "bg-gray-700 border-gray-600 text-white"
                            : "bg-white border-gray-300 text-gray-900"
                        }`}
                      />
                    </div>
                  )}

                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        isDark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Equipment Requirement
                    </label>
                    <div className="relative">
                      <textarea
                        disabled={isDisabled}
                        rows={2}
                        value={formData.equipmentCapability}
                        onChange={(e) =>
                          handleInputChange("equipmentCapability", e.target.value)
                        }
                        className={`w-full px-4 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none ${
                          isDark
                            ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                            : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                        }`}
                      />
                      <FaEdit className="absolute right-3 top-3 text-gray-400" />
                    </div>
                  </div>

                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        isDark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Upload Document
                    </label>

                    <div className="flex gap-2 items-start">
                      <input
                        type="file"
                        disabled={isDisabled}
                        onChange={(e) => setSiteVisitSelectedFile(e.target.files?.[0] || null)}
                        className={`flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                          isDark
                            ? "bg-gray-700 border-gray-600 text-white file:bg-gray-600 file:text-white file:border-0 file:mr-3 file:px-3 file:py-1 file:rounded"
                            : "bg-white border-gray-300 text-gray-900 file:bg-gray-100 file:text-gray-700 file:border-0 file:mr-3 file:px-3 file:py-1 file:rounded"
                        }`}
                      />

                      {!isDisabled && (
                        <div className="flex gap-1">
                          <button
                            type="button"
                            onClick={uploadSiteVisitDoc}
                            className="p-2 rounded bg-blue-500 text-white hover:bg-blue-600"
                          >
                            <FaPlus />
                          </button>

                          <button
                            type="button"
                            onClick={removeSiteVisitDoc}
                            className="p-2 rounded bg-red-500 text-white hover:bg-red-600"
                          >
                            <FaMinus />
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="mt-3">
                      <select
                        size={4}
                        value={siteVisitSelectedIndex}
                        onChange={(e) => setSiteVisitSelectedIndex(parseInt(e.target.value, 10))}
                        className={`w-full px-3 py-2 border rounded-lg text-sm ${
                          isDark
                            ? "bg-gray-700 border-gray-600 text-white"
                            : "bg-white border-gray-300 text-gray-900"
                        }`}
                      >
                        {siteVisitFiles.map((f, idx) => (
                          <option key={f + idx} value={idx}>
                            {f}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        isDark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Technical Qualification
                    </label>
                    <div className="relative">
                      <textarea
                        rows={2}
                        disabled={isDisabled}
                        value={formData.technicalQualification}
                        onChange={(e) =>
                          handleInputChange("technicalQualification", e.target.value)
                        }
                        className={`w-full px-4 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none ${
                          isDark
                            ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                            : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                        }`}
                      />
                      <FaEdit className="absolute right-3 top-3 text-gray-400" />
                    </div>
                  </div>

                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        isDark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Site Visit & Pre Bid Meeting Remarks
                    </label>
                    <div className="relative">
                      <textarea
                        rows={2}
                        disabled={isDisabled}
                        value={formData.siteVisitPreBidRemarks}
                        onChange={(e) =>
                          handleInputChange("siteVisitPreBidRemarks", e.target.value)
                        }
                        className={`w-full px-4 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none ${
                          isDark
                            ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                            : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                        }`}
                      />
                      <FaEdit className="absolute right-3 top-3 text-gray-400" />
                    </div>
                  </div>

                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        isDark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Manpower Requirement
                    </label>
                    <div className="relative">
                      <textarea
                        rows={2}
                        disabled={isDisabled}
                        value={formData.manpowerCapability}
                        onChange={(e) => handleInputChange("manpowerCapability", e.target.value)}
                        className={`w-full px-4 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none ${
                          isDark
                            ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                            : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                        }`}
                      />
                      <FaEdit className="absolute right-3 top-3 text-gray-400" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Guarantee Details Section */}
            <div
              className={`p-4 rounded-lg border ${
                isDark ? "bg-gray-800 border-gray-700" : "bg-blue-50 border-blue-200"
              }`}
            >
              <h2 className={`text-lg font-semibold mb-4 ${isDark ? "text-white" : "text-blue-900"}`}>
                GUARANTEE DETAILS
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        isDark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Mobilisation Advance
                    </label>
                    <div className="relative">
                      <textarea
                        rows={2}
                        disabled={isDisabled}
                        value={formData.mobilisationAdvance}
                        onChange={(e) => handleInputChange("mobilisationAdvance", e.target.value)}
                        className={`w-full px-4 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none ${
                          isDark
                            ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                            : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                        }`}
                      />
                      <FaEdit className="absolute right-3 top-3 text-gray-400" />
                    </div>
                  </div>

                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        isDark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Performance Guarantee
                    </label>
                    <div className="relative">
                      <textarea
                        rows={2}
                        disabled={isDisabled}
                        value={formData.performanceGuarantee}
                        onChange={(e) =>
                          handleInputChange("performanceGuarantee", e.target.value)
                        }
                        className={`w-full px-4 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none ${
                          isDark
                            ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                            : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                        }`}
                      />
                      <FaEdit className="absolute right-3 top-3 text-gray-400" />
                    </div>
                  </div>

                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        isDark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Retention Money
                    </label>
                    <div className="relative">
                      <textarea
                        rows={2}
                        disabled={isDisabled}
                        value={formData.retentionMoney}
                        onChange={(e) => handleInputChange("retentionMoney", e.target.value)}
                        className={`w-full px-4 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none ${
                          isDark
                            ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                            : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                        }`}
                      />
                      <FaEdit className="absolute right-3 top-3 text-gray-400" />
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        isDark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Bid Validity *
                    </label>

                    <div className="grid grid-cols-3 gap-2 items-center">
                      <input
                        type="number"
                        disabled={isDisabled}
                        placeholder="Days"
                        value={formData.bidValidityDays}
                        onChange={(e) => handleInputChange("bidValidityDays", e.target.value)}
                        className={`px-4 py-2 border rounded-lg ${
                          isDark
                            ? "bg-gray-700 border-gray-600 text-white"
                            : "bg-white border-gray-300 text-gray-900"
                        }`}
                      />

                      <input
                        type="date"
                        disabled={isDisabled}
                        value={formData.bidValidityFrom}
                        onChange={(e) => handleInputChange("bidValidityFrom", e.target.value)}
                        className={`px-4 py-2 border rounded-lg ${
                          isDark
                            ? "bg-gray-700 border-gray-600 text-white"
                            : "bg-white border-gray-300 text-gray-900"
                        }`}
                      />

                      <input
                        type="date"
                        value={formData.bidValidityTo}
                        readOnly
                        className={`px-4 py-2 border rounded-lg ${
                          isDark
                            ? "bg-gray-600 border-gray-600 text-white"
                            : "bg-gray-100 border-gray-300 text-gray-700"
                        }`}
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        isDark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Release of PBG
                    </label>
                    <div className="relative">
                      <textarea
                        rows={2}
                        disabled={isDisabled}
                        value={formData.releaseOfPbg}
                        onChange={(e) => handleInputChange("releaseOfPbg", e.target.value)}
                        className={`w-full px-4 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none ${
                          isDark
                            ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                            : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                        }`}
                      />
                      <FaEdit className="absolute right-3 top-3 text-gray-400" />
                    </div>
                  </div>

                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        isDark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Release of Retention Money
                    </label>
                    <div className="relative">
                      <textarea
                        rows={2}
                        disabled={isDisabled}
                        value={formData.releaseOfRetentionMoney}
                        onChange={(e) =>
                          handleInputChange("releaseOfRetentionMoney", e.target.value)
                        }
                        className={`w-full px-4 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none ${
                          isDark
                            ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                            : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                        }`}
                      />
                      <FaEdit className="absolute right-3 top-3 text-gray-400" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Tax Details Section */}
            <div
              className={`p-4 rounded-lg border ${
                isDark ? "bg-gray-800 border-gray-700" : "bg-blue-50 border-blue-200"
              }`}
            >
              <div className="flex justify-between items-center mb-4">
                <h2 className={`text-lg font-semibold ${isDark ? "text-white" : "text-blue-900"}`}>
                  TAX DETAILS
                </h2>
                <div className="text-right">
                  <h3 className={`text-md font-semibold ${isDark ? "text-white" : "text-blue-900"}`}>
                    MAJOR ITEMS OF WORK INVOLVED
                  </h3>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <table className="w-full border border-gray-300">
                    <thead className={`${buttonClass} text-white`}>
                      <tr>
                        <th className="border border-gray-300 px-2 py-2 text-sm">Tax Type</th>
                        <th className="border border-gray-300 px-2 py-2 text-sm">%</th>
                        <th className="border border-gray-300 px-2 py-2 text-sm">Description</th>
                      </tr>
                    </thead>

                    <tbody>
                      {taxDetails.map((row, index) => {
                        const isOthers = String(row.taxtypeId) === "123456789";
                        return (
                          <tr key={index} className={isDark ? "bg-gray-700" : "bg-white"}>
                            <td className="border border-gray-300 px-2 py-1">
                              <select
                                disabled={isDisabled}
                                value={row.taxtypeId}
                                onChange={(e) =>
                                  handleTaxChange(index, "taxtypeId", e.target.value)
                                }
                                className={`w-full px-2 py-1 text-sm border-0 ${
                                  isDark ? "bg-gray-700 text-white" : "bg-white text-gray-900"
                                }`}
                              >
                                <option value="0">Select</option>
                                {taxTypeOptions.map((t) => (
                                  <option key={t.id} value={t.id}>
                                    {t.name}
                                  </option>
                                ))}
                                <option value="123456789">Others</option>
                              </select>

                              {isOthers && !isDisabled && (
                                <input
                                  className={`mt-1 w-full px-2 py-1 text-sm border rounded ${
                                    isDark
                                      ? "bg-gray-700 border-gray-600 text-white"
                                      : "bg-white border-gray-300"
                                  }`}
                                  placeholder="Enter Tax Type"
                                  value={row.otherTaxTypeName}
                                  onChange={(e) =>
                                    handleTaxChange(index, "otherTaxTypeName", e.target.value)
                                  }
                                />
                              )}
                            </td>

                            <td className="border border-gray-300 px-2 py-1">
                              <input
                                type="text"
                                disabled={isDisabled}
                                value={row.description1}
                                onChange={(e) =>
                                  handleTaxChange(index, "description1", e.target.value)
                                }
                                className={`w-full px-2 py-1 text-sm border-0 ${
                                  isDark ? "bg-gray-700 text-white" : "bg-white text-gray-900"
                                }`}
                              />
                            </td>

                            <td className="border border-gray-300 px-2 py-1">
                              <input
                                type="text"
                                disabled={isDisabled}
                                value={row.description}
                                onChange={(e) =>
                                  handleTaxChange(index, "description", e.target.value)
                                }
                                className={`w-full px-2 py-1 text-sm border-0 ${
                                  isDark ? "bg-gray-700 text-white" : "bg-white text-gray-900"
                                }`}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>

                  <div className="mt-2">
                    <textarea
                      placeholder="Shop Drawing"
                      rows={3}
                      value={formData.shopDrawing}
                      disabled={isDisabled}
                      onChange={(e) => handleInputChange("shopDrawing", e.target.value)}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                          : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                      }`}
                    />
                  </div>
                </div>

                <div>
                  {!isDisabled && (
                    <div className="flex gap-2 mb-2">
                      <button
                        type="button"
                        onClick={addWorkRow}
                        className="px-3 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600"
                      >
                        <FaPlus />
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          workRows.length > 1 && removeWorkRow(workRows.length - 1)
                        }
                        className="px-3 py-1 bg-red-500 text-white rounded text-sm hover:bg-red-600"
                      >
                        <FaMinus />
                      </button>
                    </div>
                  )}

                  <div
                    className={`p-3 rounded-lg border ${
                      isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
                    }`}
                  >
                    <div className="grid grid-cols-12 gap-2 text-xs font-semibold mb-2">
                      <div className="col-span-7">Work Item</div>
                      <div className="col-span-3">UOM</div>
                      <div className="col-span-2">Qty</div>
                    </div>

                    <div className="space-y-2">
                      {workRows.map((row, idx) => {
                        const isWorkOther = row.workItemId === "10236589";
                        const isUomOther = row.uomId === "01";

                        return (
                          <div key={idx} className="grid grid-cols-1 md:grid-cols-12 gap-2">
                            <div className="md:col-span-7">
                              <select
                                disabled={isDisabled}
                                value={row.workItemId}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  updateWorkRow(idx, "workItemId", v);
                                  if (v !== "10236589")
                                    updateWorkRow(idx, "workItemOther", "");
                                }}
                                className={`w-full px-3 py-2 rounded border text-sm ${
                                  isDark
                                    ? "bg-gray-700 border-gray-600 text-white"
                                    : "bg-white border-gray-300 text-gray-900"
                                }`}
                              >
                                <option value="0">Select</option>

                                {workItemList.map((x) => (
                                  <option key={x.id} value={x.id} title={x.name}>
                                    {truncate(x.name, 65)}
                                  </option>
                                ))}

                                <option value="10236589">Others</option>
                              </select>

                              {isWorkOther && (
                                <input
                                  type="text"
                                  disabled={isDisabled}
                                  value={row.workItemOther}
                                  onChange={(e) =>
                                    updateWorkRow(idx, "workItemOther", e.target.value)
                                  }
                                  className={`mt-2 w-full px-3 py-2 rounded border text-sm ${
                                    isDark
                                      ? "bg-gray-700 border-gray-600 text-white"
                                      : "bg-white border-gray-300 text-gray-900"
                                  }`}
                                  placeholder="Enter Other Work Item"
                                />
                              )}
                            </div>

                            <div className="md:col-span-3">
                              <select
                                value={row.uomId}
                                disabled={isDisabled}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  updateWorkRow(idx, "uomId", v);
                                  if (v !== "01") updateWorkRow(idx, "uomOther", "");
                                }}
                                className={`w-full px-3 py-2 rounded border text-sm ${
                                  isDark
                                    ? "bg-gray-700 border-gray-600 text-white"
                                    : "bg-white border-gray-300 text-gray-900"
                                }`}
                              >
                                <option value="0">Select</option>

                                {uomList.map((x) => (
                                  <option key={x.id} value={x.id} title={x.name}>
                                    {truncate(x.name, 30)}
                                  </option>
                                ))}

                                <option value="01">Others</option>
                              </select>

                              {isUomOther && (
                                <input
                                  type="text"
                                  disabled={isDisabled}
                                  value={row.uomOther}
                                  onChange={(e) =>
                                    updateWorkRow(idx, "uomOther", e.target.value)
                                  }
                                  className={`mt-2 w-full px-3 py-2 rounded border text-sm ${
                                    isDark
                                      ? "bg-gray-700 border-gray-600 text-white"
                                      : "bg-white border-gray-300 text-gray-900"
                                  }`}
                                  placeholder="Enter Other UOM"
                                />
                              )}
                            </div>

                            <div className="md:col-span-2">
                              <input
                                type="number"
                                disabled={isDisabled}
                                value={row.qty}
                                onChange={(e) => updateWorkRow(idx, "qty", e.target.value)}
                                className={`w-full px-3 py-2 rounded border text-sm ${
                                  isDark
                                    ? "bg-gray-700 border-gray-600 text-white"
                                    : "bg-white border-gray-300 text-gray-900"
                                }`}
                                placeholder="Qty"
                              />
                            </div>

                            <div className="md:col-span-12 flex justify-end">
                              {workRows.length > 1 && !isDisabled && (
                                <button
                                  type="button"
                                  onClick={() => removeWorkRow(idx)}
                                  className="text-xs text-red-500 hover:underline"
                                >
                                  Remove row
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Other Details and Quotation Details Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div
                className={`p-4 rounded-lg border ${
                  isDark ? "bg-gray-800 border-gray-700" : "bg-blue-50 border-blue-200"
                }`}
              >
                <div className="flex items-center justify-between mb-4">
                  <h2 className={`text-lg font-semibold ${isDark ? "text-white" : "text-blue-900"}`}>
                    OTHER DETAILS
                  </h2>

                  {!isDisabled && (
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={addOtherRow}
                        className="px-3 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600"
                      >
                        <FaPlus />
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          otherRows.length > 1 && removeOtherRow(otherRows.length - 1)
                        }
                        className="px-3 py-1 bg-red-500 text-white rounded text-sm hover:bg-red-600"
                      >
                        <FaMinus />
                      </button>
                    </div>
                  )}
                </div>

                <div className="space-y-3">
                  {otherRows.map((r, idx) => {
                    const isOthers = r.otherDetailsId === "OTHERS";
                    return (
                      <div key={idx} className="grid grid-cols-1 md:grid-cols-3 gap-2 items-center">
                        <div>
                          <select
                            value={r.otherDetailsId}
                            disabled={isDisabled}
                            onChange={(e) =>
                              updateOtherRow(idx, {
                                otherDetailsId: e.target.value,
                                otherDetailsText: "",
                              })
                            }
                            className={`w-full px-2 py-2 text-sm border rounded ${
                              isDark
                                ? "bg-gray-700 border-gray-600 text-white"
                                : "bg-white border-gray-300 text-gray-900"
                            }`}
                          >
                            <option value="0">Select</option>
                            {otherMaster.map((m) => (
                              <option key={m.id} value={String(m.id)}>
                                {m.name}
                              </option>
                            ))}
                            <option value="OTHERS">Others</option>
                          </select>

                          {isOthers && (
                            <input
                              type="text"
                              disabled={isDisabled}
                              value={r.otherDetailsText}
                              onChange={(e) =>
                                updateOtherRow(idx, { otherDetailsText: e.target.value })
                              }
                              placeholder="Enter other detail name"
                              className={`mt-2 w-full px-2 py-2 text-sm border rounded ${
                                isDark
                                  ? "bg-gray-700 border-gray-600 text-white"
                                  : "bg-white border-gray-300 text-gray-900"
                              }`}
                            />
                          )}
                        </div>

                        <input
                          type="text"
                          disabled={isDisabled}
                          value={r.item1}
                          onChange={(e) => updateOtherRow(idx, { item1: e.target.value })}
                          placeholder="Item 1"
                          className={`w-full px-2 py-2 text-sm border rounded ${
                            isDark
                              ? "bg-gray-700 border-gray-600 text-white"
                              : "bg-white border-gray-300 text-gray-900"
                          }`}
                        />

                        <input
                          type="text"
                          disabled={isDisabled}
                          value={r.item2}
                          onChange={(e) => updateOtherRow(idx, { item2: e.target.value })}
                          placeholder="Item 2"
                          className={`w-full px-2 py-2 text-sm border rounded ${
                            isDark
                              ? "bg-gray-700 border-gray-600 text-white"
                              : "bg-white border-gray-300 text-gray-900"
                          }`}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>

              <div
                className={`p-4 rounded-lg border ${
                  isDark ? "bg-gray-800 border-gray-700" : "bg-blue-50 border-blue-200"
                }`}
              >
                <div className="flex items-center justify-between mb-4">
                  <h2 className={`text-lg font-semibold ${isDark ? "text-white" : "text-blue-900"}`}>
                    QUOTATION DETAILS
                  </h2>

                  {!isDisabled && (
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={addSupplierRow}
                        className="px-3 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600"
                      >
                        <FaPlus />
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          supplierRows.length > 1 &&
                          removeSupplierRow(supplierRows.length - 1)
                        }
                        className="px-3 py-1 bg-red-500 text-white rounded text-sm hover:bg-red-600"
                      >
                        <FaMinus />
                      </button>
                    </div>
                  )}
                </div>

                <div className="space-y-4">
                  {supplierRows.map((row, sIdx) => (
                    <div
                      key={sIdx}
                      className={`p-3 rounded border ${
                        isDark ? "border-gray-700" : "border-blue-200"
                      }`}
                    >
                      <label
                        className={`block text-sm font-medium mb-2 ${
                          isDark ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Supplier
                      </label>
                      <input
                        type="text"
                        disabled={isDisabled}
                        value={row.supplierName}
                        onChange={(e) =>
                          updateSupplierRow(sIdx, { supplierName: e.target.value })
                        }
                        className={`w-full px-3 py-2 text-sm border rounded ${
                          isDark
                            ? "bg-gray-700 border-gray-600 text-white"
                            : "bg-white border-gray-300 text-gray-900"
                        }`}
                      />

                      <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-2 items-start">
                        <div className="md:col-span-2">
                          <label
                            className={`block text-sm font-medium mb-2 ${
                              isDark ? "text-gray-300" : "text-gray-700"
                            }`}
                          >
                            Add file
                          </label>

                          <input
                            type="file"
                            disabled={isDisabled}
                            onChange={(e) => {
                              const f = e.target.files?.[0];
                              e.target.value = "";
                              uploadSupplierFile(sIdx, f);
                            }}
                            className={`w-full px-3 py-2 border rounded ${
                              isDark
                                ? "bg-gray-700 border-gray-600 text-white file:bg-gray-600 file:text-white"
                                : "bg-white border-gray-300 text-gray-900 file:bg-gray-100 file:text-gray-700"
                            }`}
                          />
                        </div>

                        <div>
                          <label
                            className={`block text-sm font-medium mb-2 ${
                              isDark ? "text-gray-300" : "text-gray-700"
                            }`}
                          >
                            Files
                          </label>

                          <div
                            className={`h-28 overflow-auto border rounded p-2 ${
                              isDark
                                ? "bg-gray-700 border-gray-600 text-white"
                                : "bg-white border-gray-300 text-gray-900"
                            }`}
                          >
                            {row.files.length === 0 ? (
                              <div className={isDark ? "text-gray-300 text-sm" : "text-gray-500 text-sm"}>
                                No files
                              </div>
                            ) : (
                              <ul className="space-y-1">
                                {row.files.map((fn, fIdx) => (
                                  <li
                                    key={fIdx}
                                    className="flex items-center justify-between gap-2 text-xs"
                                  >
                                    <span className="truncate" title={fn}>
                                      {fn}
                                    </span>
                                    {!isDisabled && (
                                      <button
                                        type="button"
                                        onClick={() => removeSupplierFile(sIdx, fIdx)}
                                        className="px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600"
                                      >
                                        <FaTrash />
                                      </button>
                                    )}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {isViewLoaded && !isEditMode && (
              <div className="flex justify-center items-center pt-6">
                <button
                  type="button"
                  onClick={() => setIsEditMode(true)}
                  className="flex items-center gap-2 px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 bg-yellow-500 hover:bg-yellow-600 text-white"
                >
                  <FaEdit />
                  Edit
                </button>
              </div>
            )}

            {(!isViewLoaded || isEditMode) && (
              <div className="flex justify-center items-center gap-6 pt-6">
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={isSaving}
                  className={`flex items-center gap-2 px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                    isDark
                      ? `${buttonClass} ${hoverClass} text-white`
                      : `${buttonClass} ${hoverClass} text-white`
                  } ${isSaving ? "opacity-60 cursor-not-allowed" : ""}`}
                >
                  <FaSave />
                  {isSaving ? "Saving..." : "Save"}
                </button>
              </div>
            )}
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </ProjectPreOrderLayoutWrapper>
  );
};

export default TenderDetailEntry;